import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_VOUCHER_I18N_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type MarketingVoucherI18nFormModel } from "./type";
import { voucherI18nInputSchema, type VoucherI18nInputSchema } from "./validation-schema";

import type { MarketingVoucherI18nFormModelContextType } from "../ui/_context/context";

type Props = {
	inputModel: MarketingVoucherI18nFormModel["InputModel"];
	queryData: MarketingVoucherI18nFormModel["QueryData"];
};

export const useInitMarketingVoucherI18nFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<MarketingVoucherI18nFormModelContextType, "queryData"> => {
	const hotelID = _inputModel.hotelID;
	const voucherID = _inputModel.voucherID;
	const langCode = _inputModel.lang;

	const defaultInputModel = DEFAULT_PRODUCT_VOUCHER_I18N_INPUT_MODEL({ voucherID, hotelID, queryData, langCode });
	const initInput = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			MarketingVoucherI18nFormModel["InputModel"],
			ExcludeKeys,
			MarketingVoucherI18nFormModel["QueryData"],
			VoucherI18nInputSchema
		>({
			defaultInput: defaultInputModel,
			initInput,
			currentInput: initInput,
			excludeKeys: excludeKeys,
			zodSchema: voucherI18nInputSchema,
		}),
	);

	const onSetViewModel: MarketingVoucherI18nFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as MarketingVoucherI18nFormModel["ViewModel"],
				),
			} satisfies MarketingVoucherI18nFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				MarketingVoucherI18nFormModel["InputModel"],
				ExcludeKeys,
				MarketingVoucherI18nFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				MarketingVoucherI18nFormModel["InputModel"],
				ExcludeKeys,
				MarketingVoucherI18nFormModel["QueryData"],
				VoucherI18nInputSchema
			>({
				defaultInput: defaultInputModel,
				initInput,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: voucherI18nInputSchema,
			});

			setViewModel(newViewModel);
		},
		[viewModel, defaultInputModel, initInput],
	);

	return {
		viewModel,
		onSetViewModel,
	};
};
